import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['charterOn', 'timeslot', 'partySize', 'duration', 'charterType', 'charterTypeWrapper', 'addOnsSection', 'addOnPackage']
  static values = {
    updateOptionsUrl: String,
    timeslotOptions: Array,
    partySizeOptions: Array,
    durationOptions: Array,
    charterTypeOptions: Array
  }

  connect() {
    // Filter add-on packages based on initially selected charter type
    this.filterAddOnPackages()
  }

  async updateOptions() {
    const params = new URLSearchParams()

    const charterOn = this.getCharterOnValue()
    const timeslot = this.getTimeslotValue()
    const partySize = this.getPartySizeValue()
    const duration = this.getDurationValue()

    if (charterOn) params.append('charter_on', charterOn)
    if (timeslot !== null) params.append('timeslot', timeslot)
    if (partySize !== null) params.append('party_size', partySize)
    if (duration !== null) params.append('duration', duration)

    try {
      const response = await fetch(`${this.updateOptionsUrlValue}?${params.toString()}`)
      if (!response.ok) return

      const data = await response.json()

      // Update all dropdowns with new options from server
      this.updateTimeslotSelect(data.timeslot_options)
      this.updatePartySizeSelect(data.party_size_options)
      this.updateDurationSelect(data.duration_options)
      this.updateCharterType(data.charter_type_options, data.auto_select_charter_type)
    } catch (error) {
      console.error('Failed to update options:', error)
    }
  }

  getCharterOnValue() {
    if (!this.hasCharterOnTarget) return null
    const val = this.charterOnTarget.value
    return val || null
  }

  getTimeslotValue() {
    if (!this.hasTimeslotTarget) return null
    const val = parseInt(this.timeslotTarget.value)
    return isNaN(val) ? null : val
  }

  getPartySizeValue() {
    if (!this.hasPartySizeTarget) return null
    const val = parseInt(this.partySizeTarget.value)
    return isNaN(val) ? null : val
  }

  getDurationValue() {
    if (!this.hasDurationTarget) return null
    const val = parseInt(this.durationTarget.value)
    return isNaN(val) ? null : val
  }

  updateTimeslotSelect(options) {
    if (!this.hasTimeslotTarget) return
    this.updateSelectWithOptions(this.timeslotTarget, options, 'Select a time')
  }

  updatePartySizeSelect(options) {
    if (!this.hasPartySizeTarget) return
    this.updateSelectWithOptions(this.partySizeTarget, options, 'Select party size')
  }

  updateDurationSelect(options) {
    if (!this.hasDurationTarget) return
    this.updateSelectWithOptions(this.durationTarget, options, 'Select duration')
  }

  updateSelectWithOptions(selectElement, options, blankLabel) {
    const currentValue = selectElement.value
    const hadValue = currentValue !== ''
    const singleOption = options.length === 1

    // Clear existing options
    selectElement.innerHTML = ''

    // Add blank option only if multiple options
    if (!singleOption) {
      const blankOption = document.createElement('option')
      blankOption.value = ''
      blankOption.textContent = blankLabel
      selectElement.appendChild(blankOption)
    }

    // Add new options
    let currentValueStillValid = false
    options.forEach(([label, value]) => {
      const option = document.createElement('option')
      option.value = value
      option.textContent = label
      selectElement.appendChild(option)

      if (String(value) === String(currentValue)) {
        currentValueStillValid = true
      }
    })

    // Handle value restoration
    if (singleOption) {
      // Auto-select the single option
      selectElement.value = options[0][1]
    } else if (hadValue && currentValueStillValid) {
      selectElement.value = currentValue
    } else if (hadValue && !currentValueStillValid) {
      selectElement.value = ''
      // Trigger change event since the value changed
      selectElement.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  updateCharterType(charterTypeOptions, autoSelectType) {
    if (!this.hasCharterTypeTarget) return

    const select = this.charterTypeTarget
    const currentValue = select.value

    if (autoSelectType) {
      // Auto-select the only compatible type
      this.rebuildCharterTypeOptions(select, charterTypeOptions, false)
      select.value = autoSelectType.id
      select.disabled = true
      this.addHiddenCharterTypeInput(autoSelectType.id)

      // Show helper text
      this.showAutoSelectedHelper(autoSelectType.name)

      // Trigger change if value changed
      if (String(currentValue) !== String(autoSelectType.id)) {
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    } else if (charterTypeOptions.length > 1) {
      // Multiple types still valid - enable dropdown and filter options
      select.disabled = false
      this.removeHiddenCharterTypeInput()
      this.hideAutoSelectedHelper()

      this.rebuildCharterTypeOptions(select, charterTypeOptions, true)

      // Restore or clear value
      const currentValueStillValid = charterTypeOptions.some(ct => String(ct.id) === String(currentValue))
      if (currentValueStillValid) {
        select.value = currentValue
      } else {
        select.value = ''
        if (currentValue !== '') {
          select.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    } else {
      // No compatible types
      select.disabled = false
      this.removeHiddenCharterTypeInput()
      this.hideAutoSelectedHelper()
      this.rebuildCharterTypeOptions(select, [], true)
      if (currentValue !== '') {
        select.value = ''
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    // Filter add-on packages based on the updated charter type
    this.filterAddOnPackages()
  }

  rebuildCharterTypeOptions(select, charterTypeOptions, includeBlank) {
    select.innerHTML = ''

    if (includeBlank) {
      const blankOption = document.createElement('option')
      blankOption.value = ''
      blankOption.textContent = 'Select a charter type'
      select.appendChild(blankOption)
    }

    charterTypeOptions.forEach(ct => {
      const option = document.createElement('option')
      option.value = ct.id
      option.textContent = ct.name
      select.appendChild(option)
    })
  }

  showAutoSelectedHelper(typeName) {
    if (!this.hasCharterTypeWrapperTarget) return

    let helper = this.charterTypeWrapperTarget.querySelector('.auto-selected-helper')
    if (!helper) {
      helper = document.createElement('small')
      helper.className = 'auto-selected-helper text-muted d-block mt-1'
      this.charterTypeWrapperTarget.appendChild(helper)
    }
    helper.textContent = `Auto-selected based on your choices`
  }

  hideAutoSelectedHelper() {
    if (!this.hasCharterTypeWrapperTarget) return

    const helper = this.charterTypeWrapperTarget.querySelector('.auto-selected-helper')
    if (helper) {
      helper.remove()
    }
  }

  addHiddenCharterTypeInput(value) {
    this.removeHiddenCharterTypeInput() // Remove any existing one first
    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = this.charterTypeTarget.name
    hidden.value = value
    hidden.id = 'charter_type_hidden_input'
    this.charterTypeTarget.parentNode.appendChild(hidden)
  }

  removeHiddenCharterTypeInput() {
    const hidden = document.getElementById('charter_type_hidden_input')
    if (hidden) hidden.remove()
  }

  filterAddOnPackages() {
    if (!this.hasAddOnPackageTarget) return

    const selectedCharterTypeId = this.hasCharterTypeTarget ? this.charterTypeTarget.value : null

    this.addOnPackageTargets.forEach(packageEl => {
      const packageCharterTypeId = packageEl.dataset.charterTypeId
      if (!selectedCharterTypeId || packageCharterTypeId !== selectedCharterTypeId) {
        packageEl.style.display = 'none'
        // Uncheck any checkboxes in hidden packages
        packageEl.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
          cb.checked = false
        })
      } else {
        packageEl.style.display = ''
      }
    })

    // Show/hide the entire add-ons section based on whether any packages are visible
    if (this.hasAddOnsSectionTarget) {
      const hasVisiblePackages = this.addOnPackageTargets.some(el => el.style.display !== 'none')
      this.addOnsSectionTarget.style.display = hasVisiblePackages ? '' : 'none'
    }
  }
}
